/**
  * Represents the concept of a search filter.
  * Filters have a label, values, and children.
  * Values have name, enabled flag, and match count.
  * Children are nested Filters representing a hierarchy.
  */
export default class Filter {
  static defaultProps = {
    label: '', // a name for this filter
    values: [], // string[] - a list of possible values for this filter

    // For API searches, each filter value will be associated with a
    // query param when making API requests. Here the index should
    // match in the values and queryParams arrays.
    queryParams: [],

    // A function that performs the actual
    // filtering of products for this filter.
    // Allows the filtering logic to be specified when
    // the Filter instance is created. May not apply
    // to server controlled filters. The default function
    // here just returns the products array unchaged.
    onApply: (products, enabledValues) => { return products; }
  }

  /** Default constructs a Filter with optional overrides. */
  constructor (overrides) {
    this.fields = { ...Filter.defaultProps, ...overrides };
  }

  /** Creates a new Filter copy with optional overrides. */
  makeCopy (overrides) {
    return new Filter({ ...this.fields, ...overrides });
  }

  /**
   * Returns the label for this Filter.
   * @returns {string} label - The filter label
   */
  label () {
    return this.fields.label;
  }

  /**
   * Returns the values for this Filter.
   * @returns {string[]} values - The possible filter values
   */
  values () {
    return this.fields.values;
  }

  /**
   * Returns the query params matching a values list.
   * @param {string[]} enabledValues - The input values
   * @returns {string[]} queryParams - The matching query params
   */
  queryParamsFor (enabledValues) {
    let queryParams = [];
    enabledValues.forEach(enabledValue => {
      const index = this.fields.values.findIndex(value => value === enabledValue);
      if (index >= 0 && index < this.fields.queryParams.length) {
        queryParams.push(this.fields.queryParams[index]);
      }
    });

    return queryParams;
  }

  /**
   * Executes the onApply callback.
   * @returns {Product[]} products - Products that matched the fitler.
   */
  apply (products, enabledValues) {
    return this.fields.onApply(products, enabledValues);
  }
}
